'use client';

import { Button, Stack, Text } from '@slims/ui/atoms';
import { apiFetch } from '@/lib/utils/api';
import { useState } from 'react';
import { toast } from 'sonner';
import { type AdminPick, AdminUserPicker } from '@/components/admin/admin-user-picker';

const emptyPick: AdminPick = {
	email: '',
	fullName: '',
	password: '',
	existing: null,
	emailValid: false,
	checking: false,
};

/**
 * Add a user to an org (CER or configuration) with a given role. The form asks
 * for the email first and only requests the extra details (name + password)
 * when the email has no account yet — see {@link AdminUserPicker}. The submit
 * label adapts. `basePath` is the org's user API root, e.g.
 * `/api/admin/cers/:id` or `/api/admin/configs/:id`.
 */
export function AddCerUser({
	basePath,
	role,
	roleLabel,
	onAdded,
}: {
	basePath: string;
	role: 'admin' | 'referent';
	roleLabel: string;
	onAdded: () => void;
}) {
	const [pick, setPick] = useState<AdminPick>(emptyPick);
	const [resetKey, setResetKey] = useState(0);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');

	async function handleSubmit() {
		setError('');
		if (!pick.emailValid) {
			setError('Inserisci un indirizzo email valido');
			return;
		}
		if (!pick.existing && pick.password.length < 8) {
			setError('La password deve avere almeno 8 caratteri');
			return;
		}
		setSaving(true);
		const body = pick.existing
			? { userId: pick.existing.id, role }
			: { email: pick.email, password: pick.password, fullName: pick.fullName, role };
		const res = await apiFetch(`${basePath}/users`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		});
		setSaving(false);
		if (!res.ok) {
			const b = (await res.json().catch(() => ({}))) as { error?: string };
			setError(b.error ?? 'Errore nel salvataggio');
			return;
		}
		// Un account nuovo riceve l'email di primo accesso: se l'invio fallisce va
		// detto, altrimenti chi lo crea aspetta un'email che non arriverà mai.
		const created = (await res.json().catch(() => ({}))) as {
			accessEmail?: { ok: boolean; error?: string };
		};
		if (pick.existing) toast.success('Utente aggiunto');
		else if (created.accessEmail?.ok)
			toast.success(`${roleLabel} creato: email di accesso inviata a ${pick.email}`);
		else
			toast.warning(
				`${roleLabel} creato, ma l'email di accesso non è partita${created.accessEmail?.error ? `: ${created.accessEmail.error}` : ''}`,
			);
		setPick(emptyPick);
		setResetKey((k) => k + 1);
		onAdded();
	}

	return (
		<Stack gap="sm">
			<AdminUserPicker
				key={resetKey}
				lookupUrl={`${basePath}/user-lookup`}
				roleLabel={roleLabel}
				onChange={setPick}
				size="sm"
			/>
			{error && (
				<Text variant="micro" tone="danger">
					{error}
				</Text>
			)}
			<Button
				type="button"
				variant="accent"
				size="sm"
				fullWidth
				onClick={handleSubmit}
				disabled={saving || pick.checking || !pick.emailValid}
			>
				{saving
					? 'Salvataggio…'
					: pick.checking
						? 'Verifica…'
						: pick.existing
							? `Aggiungi ${roleLabel}`
							: `Crea ${roleLabel}`}
			</Button>
		</Stack>
	);
}
